import { useEffect, useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import FloatingButtons from '../../components/feature/FloatingButtons';
import Breadcrumb from '../../components/feature/Breadcrumb';
import { trackPhoneClick, trackWhatsAppClick, trackFormSubmit, trackEmailClick } from '../../hooks/useAnalytics';

const submitAddr = 'https://readdy.ai/api/form/d6ljj02qoe30lj0v81ag';

export default function ContactPage() {
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    window.scrollTo(0, 0);

    document.title = 'İletişim | Fiber Ek - Fiber Optik ve Güvenlik Sistemleri İstanbul';

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Fiber Ek iletişim bilgileri. İstanbul Bağcılar\'da fiber optik kablo tamiri, güvenlik sistemleri kurulumu için bize ulaşın. Tel: +90 553 716 55 61 | info@fiberek.com');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Fiber Ek iletişim bilgileri. İstanbul Bağcılar\'da fiber optik kablo tamiri, güvenlik sistemleri kurulumu için bize ulaşın. Tel: +90 553 716 55 61 | info@fiberek.com';
      document.head.appendChild(meta);
    }

    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', 'İletişim | Fiber Ek - Fiber Optik ve Güvenlik Sistemleri İstanbul');
    } else {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:title');
      meta.content = 'İletişim | Fiber Ek - Fiber Optik ve Güvenlik Sistemleri İstanbul';
      document.head.appendChild(meta);
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', 'Fiber Ek iletişim bilgileri. Fiber optik ve güvenlik sistemleri için 7/24 hizmetinizdeyiz.');
    } else {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:description');
      meta.content = 'Fiber Ek iletişim bilgileri. Fiber optik ve güvenlik sistemleri için 7/24 hizmetinizdeyiz.';
      document.head.appendChild(meta);
    }

    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', 'https://fiberek.com/iletisim');
    } else {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:url');
      meta.content = 'https://fiberek.com/iletisim';
      document.head.appendChild(meta);
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', 'https://fiberek.com/iletisim');
    } else {
      const link = document.createElement('link');
      link.rel = 'canonical';
      link.href = 'https://fiberek.com/iletisim';
      document.head.appendChild(link);
    }

    // LocalBusiness + ContactPage Schema
    const schemaId = 'contact-page-schema';
    if (!document.getElementById(schemaId)) {
      const script = document.createElement('script');
      script.id = schemaId;
      script.type = 'application/ld+json';
      script.text = JSON.stringify([
        {
          "@context": "https://schema.org",
          "@type": "ContactPage",
          "name": "Fiber Ek İletişim",
          "url": "https://fiberek.com/iletisim",
          "description": "Fiber Ek iletişim sayfası. Fiber optik ve güvenlik sistemleri için bize ulaşın.",
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Ana Sayfa", "item": "https://fiberek.com/" },
              { "@type": "ListItem", "position": 2, "name": "İletişim", "item": "https://fiberek.com/iletisim" }
            ]
          }
        },
        {
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "Fiber Ek",
          "url": "https://fiberek.com",
          "telephone": "+90 553 716 55 61",
          "email": "info@fiberek.com",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Güneşli Mah. 1272. Sk. No: 1 İç Kapı No: 7",
            "addressLocality": "Bağcılar",
            "addressRegion": "İstanbul",
            "addressCountry": "TR"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": "41.0397",
            "longitude": "28.8457"
          },
          "openingHoursSpecification": [
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"],
              "opens": "09:00",
              "closes": "18:00"
            }
          ],
          "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+90 553 716 55 61",
            "contactType": "customer service",
            "availableLanguage": "Turkish",
            "areaServed": "TR"
          },
          "sameAs": ["https://wa.me/905537165561"]
        }
      ]);
      document.head.appendChild(script);
    }

    return () => {
      document.title = 'Fiber Ek';
      const s = document.getElementById(schemaId);
      if (s) s.remove();
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formData = new URLSearchParams();
      formData.append('name', formState.name);
      formData.append('email', formState.email);
      formData.append('phone', formState.phone);
      formData.append('subject', formState.subject);
      formData.append('message', formState.message);

      const response = await fetch(submitAddr, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        trackFormSubmit('contact_form', 'contact_page');
        setFormState({ name: '', email: '', phone: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus('idle'), 5000);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950">
      <Navbar />
      <FloatingButtons />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-slate-900/50 to-slate-950"></div>
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-cyan-500/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-cyan-400/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', path: '/' },
              { label: 'İletişim', path: '/iletisim' }
            ]}
          />

          <div className="text-center max-w-3xl mx-auto mt-8">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Bizimle <span className="text-cyan-400">İletişime Geçin</span>
            </h1>
            <p className="text-lg text-slate-300">
              Fiber optik ve güvenlik sistemleri konusunda profesyonel destek için bize ulaşın. 7/24 hizmetinizdeyiz.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Section */}
      <section className="py-16 relative">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Contact Details */}
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-white mb-8">
                  İletişim <span className="text-cyan-400">Bilgileri</span>
                </h2>
              </div>

              {/* Address */}
              <div className="flex items-start space-x-4 p-6 bg-slate-900/50 rounded-lg border border-slate-800 hover:border-cyan-500/50 transition-all">
                <div className="w-12 h-12 flex items-center justify-center bg-cyan-500/10 rounded-lg flex-shrink-0">
                  <i className="ri-map-pin-line text-2xl text-cyan-400"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Adres</h3>
                  <p className="text-slate-300 leading-relaxed">
                    Güneşli Mah. 1272. Sk. No: 1<br />
                    İç Kapı No: 7<br />
                    Bağcılar/İstanbul
                  </p>
                </div>
              </div>

              {/* Phone */}
              <div className="flex items-start space-x-4 p-6 bg-slate-900/50 rounded-lg border border-slate-800 hover:border-cyan-500/50 transition-all">
                <div className="w-12 h-12 flex items-center justify-center bg-cyan-500/10 rounded-lg flex-shrink-0">
                  <i className="ri-phone-line text-2xl text-cyan-400"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Telefon</h3>
                  <a 
                    href="tel:+905537165561" 
                    onClick={() => trackPhoneClick('+905537165561', 'contact_page_card')}
                    className="text-slate-300 hover:text-cyan-400 transition-colors text-lg whitespace-nowrap"
                  >
                    +90 553 716 55 61
                  </a>
                  <p className="text-sm text-slate-400 mt-1">7/24 Destek Hattı</p>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-start space-x-4 p-6 bg-slate-900/50 rounded-lg border border-slate-800 hover:border-cyan-500/50 transition-all">
                <div className="w-12 h-12 flex items-center justify-center bg-cyan-500/10 rounded-lg flex-shrink-0">
                  <i className="ri-mail-line text-2xl text-cyan-400"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">E-posta</h3>
                  <a
                    href="mailto:info@fiberek.com"
                    onClick={() => trackEmailClick('info@fiberek.com', 'contact_page_card')}
                    className="text-slate-300 hover:text-cyan-400 transition-colors whitespace-nowrap"
                  >
                    info@fiberek.com
                  </a>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <a
                  href="tel:+905537165561"
                  onClick={() => trackPhoneClick('+905537165561', 'contact_page_cta')}
                  className="flex items-center justify-center space-x-2 px-6 py-3 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-all font-medium whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-line text-xl"></i>
                  <span>Hemen Ara</span>
                </a>
                <a
                  href="https://wa.me/905537165561"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackWhatsAppClick('+905537165561', 'contact_page_cta')}
                  className="flex items-center justify-center space-x-2 px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all font-medium whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-whatsapp-line text-xl"></i>
                  <span>WhatsApp</span>
                </a>
              </div>
            </div>

            {/* Google Maps */}
            <div className="h-[600px] rounded-lg overflow-hidden border border-slate-800 shadow-xl">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3009.8447891234567!2d28.8456789!3d41.0397!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDHCsDAyJzIzLjAiTiAyOMKwNTAnNDQuNCJF!5e0!3m2!1str!2str!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Fiber ve Güvenlik Çözümleri Konum"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* Working Hours */}
      <section className="py-16 bg-slate-900/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-4">
                Çalışma <span className="text-cyan-400">Saatleri</span>
              </h2>
              <p className="text-slate-300">
                Acil durumlar için 7/24 hizmetinizdeyiz
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="p-6 bg-slate-900/50 rounded-lg border border-slate-800">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white">Hafta İçi</h3>
                  <i className="ri-time-line text-2xl text-cyan-400"></i>
                </div>
                <p className="text-slate-300 text-lg">09:00 - 18:00</p>
                <p className="text-sm text-slate-400 mt-2">Pazartesi - Cuma</p>
              </div>

              <div className="p-6 bg-slate-900/50 rounded-lg border border-slate-800">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white">Acil Servis</h3>
                  <i className="ri-24-hours-line text-2xl text-cyan-400"></i>
                </div>
                <p className="text-slate-300 text-lg">7/24 Aktif</p>
                <p className="text-sm text-slate-400 mt-2">Tüm Günler</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}